import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Welcome from './components/Welcome';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import apiService from './services/apiService';
import DayDetailPage from './components/DayDetailPage';
import Article from './components/Article';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [language, setLanguage] = useState('de');
  const [loading, setLoading] = useState(true);

  // Vérifier l'authentification au chargement de l'app
  useEffect(() => {
    checkAuthentication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthentication = async () => {
    try {
      setLoading(true);
      
      // Vérifier le token patient
      const patientToken = localStorage.getItem('token');
      const savedPatientData = localStorage.getItem('user');
      
      if (patientToken && savedPatientData) {
        try {
          const response = await apiService.verifyToken();
          if (response.success) {
            const userData = JSON.parse(savedPatientData);
            setIsAuthenticated(true);
            setPatientData(userData);
            setLanguage('de');
          } else {
            // Token invalide, nettoyer le localStorage
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        } catch (error) {
          console.error('Erreur vérification token patient:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }

      // Vérifier le token admin
      const adminToken = localStorage.getItem('adminToken');
      const savedAdminData = localStorage.getItem('adminUser');
      
      if (adminToken && savedAdminData) {
        try {
          const response = await apiService.verifyToken();
          if (response.success && response.user.role === 'admin') {
            const adminUserData = JSON.parse(savedAdminData);
            setIsAdminAuthenticated(true);
            setAdminData(adminUserData);
          } else {
            // Token invalide, nettoyer le localStorage
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
          }
        } catch (error) {
          console.error('Erreur vérification token admin:', error);
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
        }
      }

      // Forcer la langue allemande pour tous les patients
      setLanguage('de');
    } catch (error) {
      console.error('Erreur vérification authentification:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setPatientData(userData);
    
    // Forcer la langue allemande pour tous les patients
    setLanguage('de');
  };

  const handleAdminLogin = (adminData) => {
    setIsAdminAuthenticated(true);
    setAdminData(adminData);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPatientData(null);
    apiService.logout();
  };

  const handleAdminLogout = () => {
    setIsAdminAuthenticated(false);
    setAdminData(null);
    apiService.logout();
  };

  const handleLanguageChange = (newLanguage) => {
    // Désactivé - langue allemande forcée
    setLanguage('de');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Routes pour les patients */}
          <Route 
            path="/login" 
            element={
              isAuthenticated ? 
                <Navigate to="/welcome" replace /> : 
                <Login onLogin={handleLogin} language={language} />
            } 
          />
          <Route 
            path="/welcome" 
            element={
              isAuthenticated ? 
                <Welcome 
                  patientData={patientData}
                  onLogout={handleLogout} 
                  language={language}
                /> : 
                <Navigate to="/login" replace />
            } 
          />

          {/* Routes pour l'administrateur */}
          <Route 
            path="/admin" 
            element={
              isAdminAuthenticated ? 
                <Navigate to="/admin/dashboard" replace /> : 
                <AdminLogin onLogin={handleAdminLogin} language={language} />
            } 
          />
          <Route 
            path="/admin/dashboard" 
            element={
              isAdminAuthenticated ? 
                <AdminDashboard 
                  onLogout={handleAdminLogout} 
                  language={language} 
                /> : 
                <Navigate to="/admin" replace />
            } 
          />

          {/* Routes pour les détails des jours */}
          <Route 
            path="/day/:phaseId/:dayNumber" 
            element={
              isAuthenticated ? (
                <DayDetailPage 
                  patientData={patientData} 
                  language={language} 
                />) : (
                <Navigate to="/login" replace />)
            }
          />

          {/* Route pour la page article */}
          <Route 
            path="/article" 
            element={
              isAuthenticated ? (
                <Article 
                  language={language} 
                />) : (
                <Navigate to="/login" replace />)
            }
          />

          {/* Routes par défaut */}
          <Route 
            path="/" 
            element={<Navigate to="/login" replace />} 
          />
          {/* Rediriger toutes les autres routes vers login */}
          <Route 
            path="*" 
            element={<Navigate to="/login" replace />} 
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App; 